// Environment configuration for secure credential management
// This ensures sensitive data is handled properly across environments

export interface AppConfig {
  okta: {
    issuer: string;
    clientId: string;
    redirectUri: string;
  };
  api: {
    tokenEndpoint: string;
    clientId: string;
    baseUrl: string;
  };
  security: {
    enableTokenCaching: boolean;
    tokenExpiryBuffer: number;
  };
}

class ConfigService {
  private config: AppConfig;

  constructor() {
    // Validate that required environment variables are present
    this.validateEnvironment();
    
    // Debug logging for environment detection
    console.log('Environment Debug:', {
      MODE: import.meta.env.MODE,
      DEV: import.meta.env.DEV,
      PROD: import.meta.env.PROD,
      NODE_ENV: process.env.NODE_ENV || 'not set'
    });
    
    this.config = {
      okta: {
        issuer: import.meta.env.VITE_OKTA_ISSUER || '',
        clientId: import.meta.env.VITE_OKTA_CLIENT_ID || '',
        redirectUri: import.meta.env.VITE_OKTA_REDIRECT_URI || window.location.origin + '/login/callback',
      },
      api: {
        tokenEndpoint: import.meta.env.VITE_TOKEN_ENDPOINT || '/api/auth/token',
        clientId: import.meta.env.VITE_API_CLIENT_ID || '', 
        baseUrl: import.meta.env.VITE_API_BASE_URL || '',
      },
      security: {
        enableTokenCaching: import.meta.env.VITE_ENABLE_TOKEN_CACHE === 'true',
        tokenExpiryBuffer: parseInt(import.meta.env.VITE_TOKEN_EXPIRY_BUFFER || '300000'), // 5 minutes default
      }
    };
  }

  private validateEnvironment(): void {
    const requiredVars = [
      'VITE_OKTA_ISSUER',
      'VITE_OKTA_CLIENT_ID',
    ];

    const missing = requiredVars.filter(varName => !import.meta.env[varName]);
    
    if (missing.length > 0) {
      console.error('Missing required environment variables:', missing);
      throw new Error(`Missing required environment variables: ${missing.join(', ')}`);
    }
  }

  getConfig(): AppConfig {
    return { ...this.config }; // Return a copy to prevent mutation
  }

  getOktaConfig() {
    return this.config.okta;
  }

  getApiConfig() {
    return this.config.api;
  }

  getSecurityConfig() {
    return this.config.security;
  }

  isDevelopment(): boolean {
    return import.meta.env.DEV;
  }

    getMODE(): string {
    return import.meta.env.MODE;
  }

  isProduction(): boolean {
    return import.meta.env.PROD;
  }

  getEnvironmentInfo() {
    return {
      MODE: import.meta.env.MODE,
      DEV: import.meta.env.DEV,
      PROD: import.meta.env.PROD,
      isDevelopment: this.isDevelopment(),
      isProduction: this.isProduction()
    };
  }
}

export const configService = new ConfigService();
export default configService;